<template lang="pug">
  StickScroll
    .filters
      .scroll
        .filter-group.sort-by(v-if="sortOptions === undefined || sortOptions.length > 0")
          h4 {{ __('Sort By') }}
          sort(:checkbox="true" :sort-options="sortOptions" @select="closeMobileFilters")
        filters-placeholder(v-if="loading")
        .filter-group(
          v-else
          v-for="(filterType, index) in visibleFilters"
          :class="'filter-group-' + filterType"
          :key="filterType"
        )
          facet(
            :class="{ 'top-filter-group': (index === 0 && activeFilterCount === 0) }"
            :filter-type="filterType"
            :facet="filters[filterType]"
          )
        button.a11y.close( @click="closeMobileFilters")
          Icon(name="x")
      .apply-filters(v-show="activeFilterCount > 0")
        RogueButton(
          color="black"
          @click="closeMobileFilters"
        ) {{ numberOfFiltersChecked > 1 ? __('Apply Filters') : __('Apply Filter') }}
          span(v-show="numberOfFiltersChecked > 0")  ({{ numberOfFiltersChecked }})
        RogueButton(
          @click="$router.replace(clearFiltersRouterLinkTo($route.query, filters))"
        ) {{ __('Clear') }}
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import sort from './sort.vue'
import facet from './facet.vue'
import filtersPlaceholder from './filters-placeholder.vue'
import { clearFiltersRouterLinkTo, getAppliedValues } from '@/js/pages/category/helpers'
import type { IReflektionFacet } from '@/js/services/rfk/types'

const route = useRoute()
const emit = defineEmits<{ (e: 'closed'): void }>()

const props = withDefaults(defineProps<{
  filters?: { [key: string]: IReflektionFacet }
  order?: string[]
  filterActions?: Record<string, unknown>
  isOpen?: boolean
  loading?: boolean
  sortOptions?: { label: string; value: string }[]
}>(), {
  filters       : () => ({}),
  order         : () => [],
  filterActions : () => ({}),
  isOpen        : false,
  loading       : false,
})

const visibleFilters = computed(() => props.order.filter(filterType => props.filters[filterType]))

const activeFilterCount = computed(() => Object.keys(route.query).filter(key => props.filters[key]).length)

const numberOfFiltersChecked = computed(() => {
  let count = 0
  Object.keys(route.query)
    .filter(key => props.filters[key])
    .forEach(key => {
      if (props.filters[key]) {
        count += getAppliedValues(key, route).length
      }
    })

  return count
})

function closeMobileFilters() {
  emit('closed')
}
</script>

<style lang='scss' scoped>
.filters {
  display: flex;
  flex-direction: column;

  @include not-desktop() {
    width: 100%;
    height: 100vh;
  }

  @include mobile() {
    padding-bottom: calc(82px + 32px);
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
  }
}

.scroll {
  @include not-desktop() {
    display: block;
    position: relative;
    padding: $space-m;
    overflow-y: auto;
    -ms-overflow-style: none;
    background-color: $color-white;

    &::-webkit-scrollbar {
      display: none;
    }
  }
}


:deep() {

  .filter-cat1,
  .filter-cat2,
  .filter-cat3,
  .filter-cat4,
  .filter-deal_type,
  .filter-category {
    .checked {
      font-weight: $weight-bold;
    }

    .checkbox .box {
      display: none;
    }
  }
}

:deep(.star, .count) {
  color: $color-gray-dark;

  &.gold {
    color: $color-yellow-dark;
  }
}

.close {
  position: absolute;
  top: $space-m;
  right: $space-m;
  cursor: pointer;

  @include all-desktop() {
    display: none;
  }
}

.filter-group.sort-by {
  border-bottom: $border-width-thin solid $color-gray-light;

  @include all-desktop() {
    display: none;
  }
}

.apply-filters {
  padding: $space-s;
  background-color: $color-white;
  box-shadow: 0 -#{$space-xxs} #{$space-xs} -#{$space-xxxs} $color-gray-dark;
  display: flex;
  justify-content: space-between;

  @include all-desktop() {
    display: none;
  }

  .button {
    width: calc(50% - #{$space-xxs});

    &:first-child {
      margin-right: $space-s;
    }
  }
}
</style>

<style lang="scss" scoped target="app">
.platform-ios,
.platform-android {
  .filters {
    padding-bottom: 0;
  }
}
</style>
